import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Package, CalendarClock, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import { deDateMedium } from "../../lib/dateFormat";
import { deCurrency } from "../../lib/numberFormat";

/**
 * Feb 2026 · Admin-Abrechnungsübersicht (reine Lese-Vorlage).
 *
 * KEINE Rechnung, KEIN Rechnungsentwurf — nur eine Zusammenfassung, damit der
 * Admin die eigentliche Rechnung manuell in sevDesk erstellen kann.
 *
 * Pro Objekt gruppiert: gebuchtes Service-Paket + Abwesenheiten dieses Objekts
 * (im gewählten Monat) + die dabei gebuchten Zusatzleistungen (als separate
 * Posten). Monatsfilter, Standard = aktueller Monat.
 *
 * Aggregiert client-seitig aus bestehenden Endpunkten:
 *   GET /bookings (Team liefert alle → nach customer_id gefiltert)
 *   GET /extras (Namen der Zusatzleistungen)
 *   GET /service-packages/bookings?customer_id=<id>
 */
const ymOf = (iso) => (iso || "").slice(0, 7);
const currentYm = () => new Date().toISOString().slice(0, 7);

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  return `${months[Number(m) - 1] || m} ${y}`;
}

function nightsBetween(from, to) {
  if (!from || !to) return 0;
  return Math.max(0, Math.round((new Date(to) - new Date(from)) / 86400000));
}

export function BillingOverviewCard({ customerId, properties }) {
  const [bookings, setBookings] = useState(null);
  const [extrasMap, setExtrasMap] = useState({});
  const [pkgBookings, setPkgBookings] = useState([]);
  const [month, setMonth] = useState(currentYm());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [b, e, p] = await Promise.all([
          api.get("/bookings"),
          api.get("/extras"),
          api.get("/service-packages/bookings", { params: { customer_id: customerId } }),
        ]);
        if (!alive) return;
        setBookings((b.data || []).filter((x) => x.customer_id === customerId));
        const map = {};
        (e.data || []).forEach((ex) => { map[ex.id] = ex; });
        setExtrasMap(map);
        setPkgBookings(p.data || []);
      } catch (err) {
        if (alive) setBookings([]);
      }
    })();
    return () => { alive = false; };
  }, [customerId]);

  // Monatsauswahl aus allen Abwesenheiten + aktueller Monat.
  const monthOptions = useMemo(() => {
    const set = new Set([currentYm()]);
    (bookings || []).forEach((b) => {
      if (b.from_date) set.add(ymOf(b.from_date));
      if (b.to_date) set.add(ymOf(b.to_date));
    });
    return Array.from(set).sort().reverse();
  }, [bookings]);

  const inMonth = (b) => {
    if (!b.from_date || !b.to_date) return false;
    return ymOf(b.from_date) <= month && ymOf(b.to_date) >= month;
  };

  const props = properties || [];
  if (props.length === 0) return null;

  const pkgFor = (propertyId) =>
    (pkgBookings || []).find((x) => x.property_id === propertyId) || null;

  return (
    <section className="mb-6 bg-white border border-navy/10 p-5" data-testid="billing-overview-card">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <ClipboardList className="w-4 h-4 text-navy-dark/60" strokeWidth={1.8} />
          <div className="label-caps">Abrechnungsübersicht</div>
        </div>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="billing-overview-month-select"
          className="border border-navy/20 px-3 py-1.5 text-[13px] text-navy bg-white focus:border-gold focus:outline-none"
        >
          {monthOptions.map((ym) => (
            <option key={ym} value={ym}>{monthLabel(ym)}</option>
          ))}
        </select>
      </div>
      <p className="text-[11.5px] text-navy-dark/55 mb-4">
        Reine Vorlage für die manuelle Rechnung in sevDesk — keine Rechnung, kein Rechnungsentwurf.
      </p>

      {bookings === null ? (
        <div className="label-caps text-navy-dark/40">Wird geladen …</div>
      ) : (
        <div className="space-y-4">
          {props.map((p) => {
            const pkg = pkgFor(p.id);
            const absences = (bookings || [])
              .filter((b) => b.property_id === p.id && b.status !== "cancelled" && inMonth(b))
              .sort((a, x) => (a.from_date || "").localeCompare(x.from_date || ""));
            return (
              <div
                key={p.id}
                data-testid={`billing-overview-property-${p.id}`}
                className="border border-navy/10"
                style={{ borderLeft: `3px solid ${BRAND_COLORS.gold}` }}
              >
                <div className="px-4 py-3 bg-navy/[0.02] border-b border-navy/10">
                  <div className="text-[14px] text-navy font-medium">{p.name || "Objekt"}</div>
                  <div className="flex items-center gap-1.5 mt-1 text-[12px] text-navy-dark/70">
                    <Package className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                    {pkg ? (
                      <span data-testid={`billing-overview-package-${p.id}`}>
                        {pkg.package_title} — {pkg.package_main}
                        {pkg.package_price != null && (
                          <span className="text-navy font-medium"> · {deCurrency(pkg.package_price, pkg.currency || "EUR")}/Woche</span>
                        )}
                      </span>
                    ) : (
                      <span className="italic text-navy-dark/45" data-testid={`billing-overview-package-${p.id}`}>Kein Service-Paket gebucht</span>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  {absences.length === 0 ? (
                    <div className="text-[12px] text-navy-dark/50 italic" data-testid={`billing-overview-no-absence-${p.id}`}>
                      Keine Abwesenheiten in {monthLabel(month)}.
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {absences.map((b) => {
                        const extras = (b.services || []).map((id) => extrasMap[id]).filter(Boolean);
                        const n = nightsBetween(b.from_date, b.to_date);
                        return (
                          <li key={b.id} data-testid={`billing-overview-absence-${b.id}`}>
                            <div className="flex items-center gap-1.5 text-[13px] text-navy">
                              <CalendarClock className="w-3.5 h-3.5 text-navy-dark/50" strokeWidth={1.6} />
                              <span className="font-medium">
                                {deDateMedium(b.from_date)} – {deDateMedium(b.to_date)}
                              </span>
                              <span className="text-navy-dark/55">· {n} {n === 1 ? "Nacht" : "Nächte"}</span>
                            </div>
                            {extras.length > 0 ? (
                              <ul className="mt-1.5 ml-5 space-y-1">
                                {extras.map((ex) => (
                                  <li
                                    key={ex.id}
                                    data-testid={`billing-overview-extra-${b.id}-${ex.id}`}
                                    className="flex items-start gap-1.5 text-[12px] text-navy-dark/80"
                                  >
                                    <Sparkles className="w-3 h-3 mt-0.5 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                                    <span>
                                      {ex.name_de}
                                      {ex.price_hint ? <span className="text-navy-dark/50"> · {ex.price_hint}</span> : null}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="mt-1 ml-5 text-[11.5px] text-navy-dark/40 italic">Keine Zusatzleistungen</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
